import { useCallback } from "react";
import { usePamFeature } from "./useFeatureFlag";
import {
  getLocalizedCurationDescription,
  getLocalizedCurationName,
  type CurationDescribedEntity,
  type CurationNamedEntity,
} from "../utils/i18n/curationTranslations";

export const CURATION_TRANSLATIONS_FLAG = "casinoPamCurationTranslations";

export type CurationDisplayName = {
  /** True once the flag has resolved to enabled. */
  isEnabled: boolean;
  /** Translated name when the flag is on and a match exists; canonical otherwise. */
  getName: (entity: CurationNamedEntity) => string;
  getDescription: (entity: CurationDescribedEntity) => string;
};

/**
 * Display-only name resolution for casino curation entities, gated behind
 * `casino_features.casino_pam_curation_translations`. Flag off (or still
 * loading) returns the canonical `name` so the UI is unchanged.
 */
export function useCurationDisplayName(): CurationDisplayName {
  const { isEnabled } = usePamFeature(CURATION_TRANSLATIONS_FLAG);
  const enabled = isEnabled === true;

  const getName = useCallback(
    (entity: CurationNamedEntity) =>
      enabled ? getLocalizedCurationName(entity) : entity.name,
    [enabled],
  );

  const getDescription = useCallback(
    (entity: CurationDescribedEntity) =>
      enabled
        ? getLocalizedCurationDescription(entity)
        : (entity.description ?? ""),
    [enabled],
  );

  return { isEnabled: enabled, getName, getDescription };
}
